import { useContext, useEffect, useRef } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { DASHBOARD_SECONDARY } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';

import { WebPanelLocationQuery, useWebPanelLocationBatchResult } from './WebPanelLocation.tsx';
import type {
	WebPanelLocationQuery$data,
	WebPanelLocationQuery$variables,
} from './__generated__/WebPanelLocationQuery.graphql.ts';
import type { WebPanel } from './WebPanelLocation.tsx';

export const useWebPanelLocation = ({
	spaceKey,
	contentId,
	location,
	// Query is preloaded on the server-side. reused the preloaded data.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	fetchPolicy = process.env.REACT_SSR ? 'cache-first' : 'cache-and-network',
	onCompleted,
}: {
	spaceKey?: string;
	contentId?: string;
	location: string;
	fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only';
	onCompleted?: () => void;
}): {
	panels: WebPanel[];
	loading: boolean;
	error: Error | null | undefined;
} => {
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();

	const spaceIdentifier =
		spaceKey || getSingleParam(queryParams, 'spaceKey') || getSingleParam(queryParams, 'key');
	const contentIdentifier = contentId || getSingleParam(queryParams, 'pageId');

	const variables = contentIdentifier
		? { contentId: contentIdentifier, location }
		: { spaceKey: spaceIdentifier, location };
	const batchResult = useWebPanelLocationBatchResult(location);
	const batchResultData = batchResult?.data;
	const batchResultError = batchResult?.error;
	const batchResultLoading = batchResult?.loading;
	const completedBatchResultKeyRef = useRef<string | null>(null);

	const baseOptions = {
		variables,
		skip: Boolean(batchResult),
		...(onCompleted && { onCompleted }),
	};

	const useNoFurtherUpdate =
		location !== DASHBOARD_SECONDARY && // Keep watching query for home route to avoid VC90 regression
		expValEquals('confluence_stop_watching_webpanellocationquery', 'isEnabled', true);

	const { loading, data, error } = useNoFurtherUpdate
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>(
				WebPanelLocationQuery,
				baseOptions,
			)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>(
				WebPanelLocationQuery,
				{
					...baseOptions,
					fetchPolicy,
				},
			);

	const result = batchResult ?? { loading, data, error };

	useEffect(() => {
		if (batchResultLoading || batchResultError || !batchResultData || !onCompleted) {
			return;
		}

		const batchResultKey = `${contentIdentifier || ''}:${spaceIdentifier || ''}:${location}`;
		if (completedBatchResultKeyRef.current !== batchResultKey) {
			completedBatchResultKeyRef.current = batchResultKey;
			onCompleted();
		}
	}, [
		batchResultData,
		batchResultError,
		batchResultLoading,
		contentIdentifier,
		location,
		onCompleted,
		spaceIdentifier,
	]);

	const panels = (result.data?.webPanels || []).filter((panel) => panel) as WebPanel[];

	return {
		panels,
		loading: result.loading,
		error: result.error,
	};
};
